import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { JOURNAL_ARTICLES } from "@/data/content";
import { Nav } from "@/components/Nav";
import { Seo } from "@/components/Seo";
import { Footer } from "@/components/Footer";
import { FadeUp } from "@/components/Reveal";

export default function JournalArticlePage() {
  const { slug } = useParams();
  const article = JOURNAL_ARTICLES.find((a) => a.slug === slug);
  const related = JOURNAL_ARTICLES.filter((a) => a.slug !== slug && a.category === article?.category).slice(0, 2);
  const more = related.length ? related : JOURNAL_ARTICLES.filter((a) => a.slug !== slug).slice(0, 2);

  if (!article) {
    return (
      <div className="bg-jawa-ivory" data-testid="journal-article-not-found">
        <Nav />
        <div className="mx-auto max-w-3xl px-6 pb-24 pt-40 text-center">
          <h1 className="font-serif text-4xl text-jawa-espresso">Story not found</h1>
          <Link to="/journal" className="mt-6 inline-flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-jawa-terracotta">
            <ArrowLeft className="h-4 w-4" /> Back to the Journal
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-jawa-ivory" data-testid={`journal-article-${article.slug}`}>
      <Nav />
      <Seo title={`${article.title} | JÀWÀ Journal`} description={article.excerpt.slice(0, 158)} />
      <header className="relative flex min-h-[50vh] items-end overflow-hidden">
        <img src={article.image} alt={article.title} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-jawa-nearblack/90 via-jawa-nearblack/35 to-jawa-nearblack/25" />
        <div className="relative mx-auto w-full max-w-4xl px-6 pb-12 pt-40 lg:px-10">
          <FadeUp>
            <p className="text-xs uppercase tracking-[0.3em] text-jawa-gold" data-testid="article-meta">
              {article.category} · {article.date}
            </p>
            <h1 className="mt-4 font-serif text-4xl font-medium tracking-tight text-jawa-ivory sm:text-5xl">
              {article.title}
            </h1>
          </FadeUp>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-16 lg:px-10">
        <FadeUp>
          <p className="font-serif text-2xl leading-snug text-jawa-espresso" data-testid="article-intro">
            {article.excerpt}
          </p>
        </FadeUp>
        <FadeUp delay={0.05}>
          <div className="mt-8 space-y-6 text-base leading-relaxed text-jawa-espresso/80" data-testid="article-body">
            {article.body.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
        </FadeUp>

        <div className="mt-16 border-t border-jawa-stone pt-10">
          <p className="text-xs uppercase tracking-[0.3em] text-jawa-coral">Keep reading</p>
          <div className="mt-6 grid gap-5 sm:grid-cols-2" data-testid="article-related">
            {more.map((a) => (
              <Link
                key={a.slug}
                to={`/journal/${a.slug}`}
                data-testid={`related-${a.slug}`}
                className="group border border-jawa-espresso/10 bg-white p-6 transition-colors hover:border-jawa-turquoise"
              >
                <p className="text-[10px] uppercase tracking-[0.25em] text-jawa-turquoise">{a.category}</p>
                <h3 className="mt-2 font-serif text-lg font-medium tracking-tight text-jawa-espresso">{a.title}</h3>
                <span className="mt-4 inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-jawa-coral">
                  Read <ArrowUpRight className="h-3.5 w-3.5" />
                </span>
              </Link>
            ))}
          </div>
          <Link
            to="/journal"
            data-testid="article-back-link"
            className="mt-10 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-jawa-espresso/60 transition-colors hover:text-jawa-coral"
          >
            <ArrowLeft className="h-4 w-4" /> All Journal stories
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}
